import React from "react";
import { Typography, TextField, Button, Stack, Grid, Box } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import AuthCard from "../../components/layout/AuthCard";
import FormHelperText from "@mui/material/FormHelperText";
import { useLocation } from "react-router-dom";
import countryCodes, { codeToFlag } from "../../constants/countries";
import ReqMark from "./ReqMark";

const ALLOWED_COUNTRIES = ["SA", "AE"];

export default function Step2Personal({
  nextStep,
  prevStep,
  defaultValues = {},
}) {
  const [firstName, setFirstName] = React.useState(
    defaultValues.firstName || ""
  );
  const [lastName, setLastName] = React.useState(defaultValues.lastName || "");
  const [phoneNumber, setphoneNumber] = React.useState(
    defaultValues.phoneNumber || ""
  );
  const [country, setCountry] = React.useState(defaultValues.country || ""); // ISO like "IN"
  const [errors, setErrors] = React.useState({});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get("role");

  const countryOptions = React.useMemo(
    () =>
      countryCodes
        .filter((c) => ALLOWED_COUNTRIES.includes(c.code))
        .map((c) => ({
          value: c.code,
          label: c.name,
          flag: codeToFlag(c.code),
        })),
    []
  );

  const selectedCountry = React.useMemo(
    () => countryOptions.find((o) => o.value === country) || null,
    [country, countryOptions]
  );

  const filterOptions = createFilterOptions({
    stringify: (o) => `${o.label} ${o.value}`,
  });

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: "rgba(255,255,255,0.06)",
      height: 50,
      px: 1.2,
      color: "#e5e7eb",
      "& fieldset": { borderColor: "rgba(148,163,184,0.18)" },
      "&:hover fieldset": { borderColor: "rgba(148,163,184,0.32)" },
      "&.Mui-focused fieldset": {
        borderColor: "#38bdf8",
        boxShadow: "0 0 0 3px rgba(56,189,248,0.25)",
      },
    },
    "& .MuiInputBase-input": { py: 0 },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(148,163,184,0.9)",
      opacity: 1,
    },
  };

  const handleNextClick = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required.";
    if (!lastName.trim()) newErrors.lastName = "Last name is required.";
    const digits = phoneNumber.replace(/\D/g, "");
    if (!phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required.";
    else if (digits.length < 9) newErrors.phoneNumber = "Phone number must have at least 9 digits.";
    else if (digits.length > 15) newErrors.phoneNumber = "Phone number must not exceed 15 digits.";
    if (!country) newErrors.country = "Please select a country.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (typeof nextStep === "function") {
      nextStep({ firstName, lastName, phoneNumber, country });
    }
  };

  return (
    <AuthCard>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6" fontWeight={700}>
          Create Account
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#38bdf8",
            borderColor: "rgba(56,189,248,0.4)",
            borderRadius: "999px",
            textTransform: "none",
            px: 2,
            height: 28,
            fontSize: 12,
            "&:hover": { background: "rgba(56,189,248,0.1)" },
          }}
        >
          {role === "investor"
            ? "Investor"
            : role === "assetOwner"
            ? "Asset Owner"
            : "Admin"}
        </Button>
      </Stack>

      <Typography color="text.secondary" sx={{ mb: 0.5 }}>
        Step 2 of 5 – Personal Information
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#f87171", mb: 2 }}>
        * All fields in this step are required.
      </Typography>

      {/* Segmented progress */}
      <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
        {[1, 2, 3, 4, 5].map((bar, index) => (
          <Box
            key={bar}
            sx={{
              flex: 1,
              height: 3,
              borderRadius: 1,
              backgroundColor: index < 2 ? "#0ea5e9" : "rgba(255,255,255,0.12)",
            }}
          />
        ))}
      </Stack>

      {/* Form */}
      <Grid container spacing={2}>
        {/* First / Last same row */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: "nowrap" }}>
            <Box flex={1} minWidth={0}>
              <Typography
                variant="body2"
                sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
              >
                First Name
                <ReqMark />
              </Typography>
              <TextField
                placeholder="John"
                fullWidth
                autoComplete="given-name"
                sx={fieldSx}
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setErrors((p) => ({ ...p, firstName: "" })); }}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography
                variant="body2"
                sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
              >
                Last Name
                <ReqMark />
              </Typography>
              <TextField
                placeholder="Doe"
                fullWidth
                autoComplete="family-name"
                sx={fieldSx}
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setErrors((p) => ({ ...p, lastName: "" })); }}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Box>
          </Stack>
        </Grid>

        {/* phoneNumber */}
        <Stack spacing={2} width="100%">
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              Phone Number
              <ReqMark />
            </Typography>
            <TextField
              type="tel"
              placeholder="+1 (555) 000-0000"
              fullWidth
              autoComplete="tel"
              sx={fieldSx}
              value={phoneNumber}
              onChange={(e) => { const v = e.target.value.replace(/[^0-9+\s-]/g, ""); setphoneNumber(v); setErrors((p) => ({ ...p, phoneNumber: "" })); }}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              Country
              <ReqMark />
            </Typography>

            <Autocomplete
              fullWidth
              options={countryOptions}
              value={selectedCountry}
              onChange={(_, v) => { setCountry(v ? v.value : ""); setErrors((p) => ({ ...p, country: "" })); }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              filterOptions={filterOptions}
              autoHighlight
              openOnFocus
              disableClearable
              popupIcon={<KeyboardArrowDownRounded />}
              disablePortal
              ListboxProps={{ style: { paddingTop: 4, paddingBottom: 4 } }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    p: 0.5,
                    bgcolor: "rgba(2,6,23,0.95)",
                    border: "1px solid rgba(148,163,184,0.18)",
                    boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
                    borderRadius: 2,
                    backdropFilter: "blur(6px)",
                  },
                },
                listbox: {
                  sx: {
                    maxHeight: 260,
                    p: 0.5,
                    "& .MuiAutocomplete-option": {
                      minHeight: 44,
                      borderRadius: 1.25,
                      px: 1.25,
                      my: 0.25,
                      color: "#e5e7eb",
                      "&[aria-selected='true']": {
                        backgroundColor: "rgba(56,189,248,0.18)",
                        border: "1px solid rgba(56,189,248,0.3)",
                      },
                      "&:hover": { backgroundColor: "rgba(56,189,248,0.12)" },
                    },
                  },
                },
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {option.flag}
                  </Box>
                  {option.label}
                </li>
              )}
              renderInput={(params) => {
                const adornment = selectedCountry ? (
                  <Box component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {selectedCountry.flag}
                  </Box>
                ) : (
                  <SearchRounded
                    sx={{
                      mr: 1,
                      fontSize: 18,
                      color: "rgba(148,163,184,0.9)",
                    }}
                  />
                );

                return (
                  <TextField
                    {...params}
                    placeholder="Select your country..."
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {adornment}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
            />
          {errors.country && (
            <FormHelperText error sx={{ mx: "14px" }}>{errors.country}</FormHelperText>
          )}
          </Grid>
        </Stack>
      </Grid>

      {/* Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          fullWidth
          onClick={prevStep}
          sx={{
            background: "#fff",
            color: "#0f172a",
            fontWeight: 600,
            borderRadius: "12px",
            "&:hover": { background: "rgba(255,255,255,0.9)" },
            textTransform: "none",
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          fullWidth
          onClick={handleNextClick}
          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Continue
        </Button>
      </Stack>
    </AuthCard>
  );
}
